// src/pages/po/ContactItemsSidePanelContent.tsx
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxesStacked } from '@/icons';
import axios from 'axios';
import { apiGet, apiPost } from '../../services/api';
import { usePaginatedFetch } from '../../hooks/usePaginatedFetch';
import ScrollableTable from '../../components/common/ScrollableTable';
import Modal from '../../components/common/Modal';
import SaveButton from '../../components/common/SaveButton';
import CloseButtonSidePanel from '../../components/common/CloseButtonSidePanel';
import SaldoRedeModalContent from './SaldoRedeModalContent';
import { ContatoItem, Contato } from '../../types/contatos/Contato';
import { SaldoRede } from '../../types/saldo/SaldoRede';
import { RequisicaoPayload } from '../../types/compras/Compra';
import { TIPOS_REQUISICAO, TipoRequisicao, PRIORIDADES, Prioridade } from '../../constants';
import './ContactItemsSidePanelContent.css';
import { formatDate } from '../../utils/formatUtils';
import { useAuth } from '../../contexts/AuthContext';
import { useApiCall } from '../../hooks/useApiCall';
import { useOperacaoLote } from '@/hooks/useOperacaoLote';
import { chaveLote, useOperacoesLote } from '@/contexts/OperacaoLoteContext';
import {
  contarSelecionados,
  fragmentoSelectAll,
  mensagemResultado,
  textoSelecao,
  type ModoSelecao,
} from '@/utils/selectAll';
import { showAlert, showConfirm } from '../../contexts/DialogContext';
import { Toast } from '../../contexts/ToastContext';
import { logger } from '@/utils/logger';

interface ContactItemsSidePanelContentProps {
  contato: Contato;
  theme: 'dark' | 'light';
  onClose: () => void;
}

/**
 * O que o usuário EDITOU numa linha. Só isso — o que ele não tocou não tem
 * entrada aqui e cai no default derivado do próprio item (ver `valorDe`).
 *
 * Antes este era um array paralelo a `items`, indexado por POSIÇÃO, preenchido
 * dentro do fetch com o default de toda linha carregada. Duas coisas quebravam
 * com isso, e a issue #31 precisa das duas resolvidas:
 *
 * 1. **Índice não é identidade.** `buildRequisicaoPayload` sempre casou por chave
 *    composta `(codProduto, codCategoriaProduto)`, então a tela convivia com duas
 *    identidades para a mesma linha. Com paginação que deduplica, o índice
 *    desalinha e a quantidade de um item aparece na linha de outro.
 * 2. **Preencher no fetch é o anti-pattern da ADR-005** — `setState` dentro da
 *    `fetchFunction` do `usePaginatedFetch` causa loop infinito (precedente em
 *    `TableIntegrationSidePanelContent`).
 *
 * Guardar só as edições resolve as duas de uma vez, e é o mesmo padrão da
 * superfície 6 (`editedQuantities` + default derivado).
 */
interface ContactItemEdits {
  quantity?: number;
  tipoRequisicao?: string;
  prioridade?: string;
  observacoes?: string;
}

/** Identidade de uma linha — a MESMA que o payload e o `excludeIds` usam. */
const chaveItem = (item: { codProduto: string; codCategoriaProduto: string }): string =>
  `${item.codProduto}|${item.codCategoriaProduto}`;

/** Defaults da tela, aplicados a toda linha que o usuário não editou. */
const TIPO_REQUISICAO_PADRAO = 'COMPRA';
const PRIORIDADE_PADRAO = 'NORMAL';

// PERDIDA em TODOS os itens faz a requisição nascer FINALIZADA, com data de
// encerramento, em vez de ABERTA — o backend calcula `todosPerdida` com allMatch.
// É o mesmo comportamento do modo enumerado, mas lá exigia marcar item a item;
// aqui sai de um seletor no rodapé mais um clique. Confirmar antes é a mesma
// regra do aviso de saturação da cotação e da cascata da conferência: consequência
// previsível não chega como surpresa.
const AVISO_PERDIDA =
  'Com o tipo PERDIDA, a requisição inteira nasce finalizada, sem passar por aprovação. Confirma?';

const ContactItemsSidePanelContent: React.FC<ContactItemsSidePanelContentProps> = ({
  contato,
  theme,
  onClose,
}) => {
  const { hasPermission, role } = useAuth();
  const [edicoes, setEdicoes] = useState<Record<string, ContactItemEdits>>({});
  const { callApi, isLoading: isSaving } = useApiCall();
  const [selectedItems, setSelectedItems] = useState<ContatoItem[]>([]);
  const [popoverData, setPopoverData] = useState<SaldoRede[]>([]);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // NENHUM setState aqui dentro — ADR-005: a `fetchFunction` do
  // usePaginatedFetch tem cascata reativa, e escrever estado nela causa loop
  // infinito. O que antes era gravado aqui (codCliente e os defaults por linha)
  // passou a ser DERIVADO da lista, mais abaixo.
  const fetchItems = useCallback(
    async (pageNumber: number, _currentItems: ContatoItem[]) => {
      const res = await apiGet(
        `/v1/contatos-connection/itens-by-contato?page=${pageNumber}&size=10&nroContato=${contato.nroContato}&codFilial=${contato.codFilial}`
      );
      return {
        items: res.data.content as ContatoItem[],
        totalItems: res.data.page.totalElements as number,
      };
    },
    [contato.nroContato, contato.codFilial]
  );

  const {
    items,
    totalItems,
    hasMore,
    isLoading: isTableLoading,
    loadMore,
    reset,
  } = usePaginatedFetch<ContatoItem>({
    fetchFunction: fetchItems,
    pageSize: 10,
    // SEM uniqueKey, de propósito. O hook deduplica por UM campo só
    // (`item[uniqueKey]`), e a identidade desta lista é COMPOSTA — o mesmo
    // codProduto aparece em categorias diferentes, e o par (codProduto,
    // codCategoriaProduto) é justamente a chave que o contrato usa em
    // `excludeIds`. Passar 'codProduto' aqui colapsaria linhas legítimas e a
    // segunda sumiria da tela.
    //
    // `undefined` acumula tudo, que é exatamente o que a paginação manual fazia
    // antes (ela só concatenava) — o comportamento visível não muda. Mesmo uso
    // de TableIntegrationSidePanelContent, citado no doc do hook.
    uniqueKey: undefined,
    initialPage: 0,
  });

  // Derivado, não gravado: o contato é um só, então qualquer item serve — e o
  // primeiro é o que a versão anterior lia, na página 0.
  const codCliente = items.length > 0 ? items[0].codCliente : null;

  useEffect(() => {
    buttonRefs.current = items.map((_, i) => buttonRefs.current[i] || null);
  }, [items]);

  /**
   * Valor que a linha exibe: o que o usuário editou, ou o default.
   *
   * Fonte ÚNICA — o mesmo `valorDe` alimenta os campos da tabela e o payload.
   * Repetir a expressão permitiria que uma linha não tocada mostrasse um número
   * e enviasse outro, que é o defeito que a review do lote A achou.
   */
  const valorDe = (item: ContatoItem): Required<ContactItemEdits> => {
    const edicao = edicoes[chaveItem(item)] ?? {};
    return {
      quantity: edicao.quantity ?? item.qtdProdutoContatoSaldo,
      tipoRequisicao: edicao.tipoRequisicao ?? TIPO_REQUISICAO_PADRAO,
      prioridade: edicao.prioridade ?? PRIORIDADE_PADRAO,
      observacoes: edicao.observacoes ?? '',
    };
  };

  const editar = (item: ContatoItem, campo: keyof ContactItemEdits, valor: string | number) => {
    const chave = chaveItem(item);
    setEdicoes((prev) => ({ ...prev, [chave]: { ...prev[chave], [campo]: valor } }));
  };

  // --- "Selecionar tudo entre páginas" (issue #31, superfície 4) --------------
  // A identidade aqui é a chave composta — a fase 1 já converteu a tela para ela,
  // e é a mesma que o `excludeIds` do contrato usa (ProdutoChave no backend).
  const [modoSelecao, setModoSelecao] = useState<ModoSelecao>('enumerado');
  const [excludeIds, setExcludeIds] = useState<Set<string>>(new Set());
  // Defaults do LOTE. No modo 'todos' o tipo, a prioridade e a observação não
  // podem ser por item: a maior parte do conjunto nunca foi carregada na tela.
  // O backend recebe um valor único de cada, aplicado a todo item resolvido
  // antes dos overrides.
  const [tipoPadraoLote, setTipoPadraoLote] = useState<string>(TIPO_REQUISICAO_PADRAO);
  const [prioridadePadraoLote, setPrioridadePadraoLote] = useState<string>(PRIORIDADE_PADRAO);
  const [obsPadraoLote, setObsPadraoLote] = useState('');
  const { disparar, estaProcessando } = useOperacaoLote();
  const { aoConcluir } = useOperacoesLote();

  // A trava do backend é por (acao, chave_pai), e aqui a chave_pai é a chave
  // composta do contato serializada — o próprio contrato documenta que ela é
  // comparada, nunca parseada.
  const chaveTrava = chaveLote('CRIAR_REQUISICAO', `${contato.nroContato}|${contato.codFilial}`);
  const processandoLote = estaProcessando(chaveTrava);

  const limparSelecao = () => {
    setModoSelecao('enumerado');
    setExcludeIds(new Set());
    setSelectedItems([]);
  };

  /** Valores distintos e não vazios já digitados numa das colunas que vão sumir. */
  const distintosEditados = (campo: 'tipoRequisicao' | 'prioridade' | 'observacoes'): string[] => {
    const vistos = new Set<string>();
    Object.values(edicoes).forEach((edicao) => {
      const valor = (edicao[campo] ?? '').toString().trim();
      if (valor !== '') vistos.add(valor);
    });
    return [...vistos];
  };

  const entrarEmTodos = async () => {
    // As três colunas editáveis somem no modo 'todos' — mas o que o usuário
    // digitou nelas NÃO pode sumir junto, em silêncio, nem sobreviver como
    // override contradizendo o campo do rodapé. Seria o defeito 3 da superfície
    // 1 outra vez: o controle sai da tela e o valor continua indo no payload,
    // agora invisível.
    //
    // Regra do dev, já aprovada na reprovação em lote e replicada na devolução:
    // um único valor distinto VIRA o valor do lote; vários disparam aviso e o
    // campo fica no default, para o usuário decidir de novo com a tela limpa.
    // Exatamente os três campos que somem da tabela — a quantidade fica de fora.
    const promocoes: Array<
      ['tipoRequisicao' | 'prioridade' | 'observacoes', (v: string) => void, string]
    > = [
      ['tipoRequisicao', setTipoPadraoLote, TIPO_REQUISICAO_PADRAO],
      ['prioridade', setPrioridadePadraoLote, PRIORIDADE_PADRAO],
      ['observacoes', setObsPadraoLote, ''],
    ];

    let houveDivergencia = false;
    for (const [campo, aplicar, padrao] of promocoes) {
      const distintos = distintosEditados(campo);
      if (distintos.length === 1) aplicar(distintos[0]);
      else if (distintos.length > 1) {
        aplicar(padrao);
        houveDivergencia = true;
      }
    }

    if (houveDivergencia) {
      await showAlert(
        'Ao selecionar todos, tipo, prioridade e observação passam a valer para o lote inteiro. Como havia valores diferentes entre as linhas, eles voltaram ao padrão — confira antes de salvar.',
        { variant: 'warning' },
      );
    }

    // As edições desses três campos saem do mapa: o rodapé passa a ser a única
    // fonte deles. A QUANTIDADE permanece — ela continua sendo por item, e o
    // rodapé não a substitui.
    setEdicoes((prev) => {
      const proximo: Record<string, ContactItemEdits> = {};
      Object.entries(prev).forEach(([chave, edicao]) => {
        if (edicao.quantity !== undefined) proximo[chave] = { quantity: edicao.quantity };
      });
      return proximo;
    });

    setModoSelecao('todos');
    setExcludeIds(new Set());
    setSelectedItems([]);
  };

  const handleSelecionarItem = (item: ContatoItem, marcado: boolean) => {
    const chave = chaveItem(item);
    if (modoSelecao === 'todos') {
      setExcludeIds((prev) => {
        const next = new Set(prev);
        if (marcado) next.delete(chave);
        else next.add(chave);
        return next;
      });
      return;
    }

    const proximos = marcado
      ? [...selectedItems, item]
      : selectedItems.filter((it) => chaveItem(it) !== chave);

    if (marcado && items.length > 0 && proximos.length === items.length) {
      void entrarEmTodos();
      return;
    }
    setSelectedItems(proximos);
  };

  const estadoCabecalho: 'vazio' | 'parcial' | 'marcado' =
    modoSelecao === 'todos'
      ? excludeIds.size === 0
        ? 'marcado'
        : 'parcial'
      : selectedItems.length === 0
        ? 'vazio'
        : 'parcial';

  const handleToggleCabecalho = () => {
    if (estadoCabecalho === 'vazio') void entrarEmTodos();
    else limparSelecao();
  };

  // TETO, não promessa (ADR-030 item 3). A view já exclui item sem saldo, então
  // aqui o total da listagem é o conjunto resolvido — mas a regra de nunca
  // prometer totalidade vale igual.
  const qtdSelecionada = contarSelecionados(
    modoSelecao,
    totalItems,
    modoSelecao === 'enumerado' ? selectedItems.length : excludeIds.size,
  );

  const temSelecao = modoSelecao === 'todos' ? qtdSelecionada > 0 : selectedItems.length > 0;

  const normalizeQuantityInput = (rawValue: string) => {
    const normalized = rawValue.replace(',', '.');
    if (!normalized || normalized === '.') return 0;
    const [integerPartRaw, decimalPartRaw = ''] = normalized.split('.');
    const integerPart = integerPartRaw === '' ? '0' : integerPartRaw;
    const decimalPart = decimalPartRaw.slice(0, 3);
    const sanitized = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
    const parsed = Number.parseFloat(sanitized);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const handleQuantityChange = (item: ContatoItem, value: string) => {
    const maxQuantity = item.qtdProdutoContatoSaldo;
    let newValue = normalizeQuantityInput(value);
    if (newValue > 0 && newValue < 0.001) newValue = 0.001;
    if (newValue > maxQuantity) newValue = maxQuantity;
    newValue = Number(newValue.toFixed(3));
    editar(item, 'quantity', newValue);
  };

  const handleTipoRequisicaoChange = (item: ContatoItem, value: string) =>
    editar(item, 'tipoRequisicao', value);

  const handlePrioridadeChange = (item: ContatoItem, value: string) =>
    editar(item, 'prioridade', value);

  const handleObservacoesChange = (item: ContatoItem, value: string) => {
    if (value.length <= 100) editar(item, 'observacoes', value);
  };

  const handleSelectChange = (newSelectedItems: ContatoItem[]) => {
    setSelectedItems(newSelectedItems);
  };

  const handleSaldoClick = async (item: ContatoItem) => {
    try {
      const res = await apiGet(
        `/v1/contatos-connection/saldo-rede?page=0&size=10&codProduto=${item.codProduto}&codCategoria=${item.codCategoriaProduto}`
      );
      setPopoverData(res.data.content);
      setPopoverOpen(true);
    } catch (err) {
      logger.error('Erro ao carregar saldo por filial:', err);
      await showAlert('Erro ao carregar o saldo. Tente novamente.', { variant: 'error' });
    }
  };

  const handleCloseModal = () => {
    setPopoverOpen(false);
    setPopoverData([]);
  };

  const buildRequisicaoPayload = async (): Promise<RequisicaoPayload | null> => {
    // Percorre os itens SELECIONADOS e lê o valor de cada um por `valorDe` — a
    // mesma função que alimenta os campos da tabela. Antes isto filtrava o array
    // paralelo `itemStates` casando por chave à mão; agora a chave é a própria
    // estrutura, e a leitura é uma só.
    for (const item of selectedItems) {
      const valor = valorDe(item);
      if (valor.quantity <= 0) {
        await showAlert('A quantidade solicitada deve ser maior que zero para itens selecionados.', { variant: 'warning' });
        return null;
      }
      if (!valor.prioridade) {
        await showAlert('A prioridade deve ser selecionada para itens selecionados.', { variant: 'warning' });
        return null;
      }
    }

    if (codCliente === null) {
      await showAlert('Não foi possível determinar o código do cliente. Tente novamente.', { variant: 'error' });
      return null;
    }

    const itens = selectedItems.map((item) => {
      const valor = valorDe(item);
      return {
        qtdSolicitada: valor.quantity,
        codProduto: item.codProduto,
        codCategoriaProduto: item.codCategoriaProduto,
        obsItem: valor.observacoes || '',
        tipoRequisicao: valor.tipoRequisicao as 'COMPRA' | 'PERDIDA',
        prioridade: valor.prioridade as 'NORMAL' | 'URGENTE',
      };
    });

    return {
      nroContato: contato.nroContato,
      status: 'ABERTA',
      origem: 'BALCAO',
      codFilial: contato.codFilial,
      codCliente,
      codUsuarioIntegracao: contato.codUsuario,
      itens,
    };
  };

  /**
   * Overrides: só as linhas VISÍVEIS que o usuário editou, e só os campos que
   * ele tocou. O backend aplica o default do lote em todo item resolvido e o
   * override por cima, sobrescrevendo apenas o campo que vem.
   *
   * A linha desmarcada é filtrada aqui: um item em `excludeIds` não pode voltar
   * ao conjunto por vir num override.
   */
  const overridesEditados = () =>
    items
      .filter((it) => !excludeIds.has(chaveItem(it)) && edicoes[chaveItem(it)] !== undefined)
      .map((it) => {
        const edicao = edicoes[chaveItem(it)];
        return {
          codProduto: it.codProduto,
          codCategoriaProduto: it.codCategoriaProduto,
          ...(edicao.quantity !== undefined ? { qtdSolicitada: edicao.quantity } : {}),
          ...(edicao.tipoRequisicao !== undefined ? { tipoRequisicao: edicao.tipoRequisicao } : {}),
          ...(edicao.prioridade !== undefined ? { prioridade: edicao.prioridade } : {}),
          ...(edicao.observacoes !== undefined ? { obsItem: edicao.observacoes } : {}),
        };
      });

  const dispararSelectAll = async () => {
    if (codCliente === null) {
      await showAlert('Não foi possível determinar o código do cliente. Tente novamente.', { variant: 'error' });
      return;
    }

    // Efeito que o modo enumerado só alcançava marcando item a item.
    if (tipoPadraoLote === 'PERDIDA' && !(await showConfirm(AVISO_PERDIDA))) return;

    const overrides = overridesEditados();
    if (overrides.some((o) => o.qtdSolicitada !== undefined && o.qtdSolicitada <= 0)) {
      await showAlert('A quantidade solicitada deve ser maior que zero para itens selecionados.', { variant: 'warning' });
      return;
    }

    const payload = {
      // Sem campo `filtro`: o WHERE desta listagem é nroContato + codFilial, e os
      // dois já são campos deste mesmo corpo. É a única superfície assim junto
      // com a remoção de cotação.
      ...fragmentoSelectAll<never, { codProduto: string; codCategoriaProduto: string }>({
        excludeIds: items
          .filter((it) => excludeIds.has(chaveItem(it)))
          .map((it) => ({ codProduto: it.codProduto, codCategoriaProduto: it.codCategoriaProduto })),
      }),
      nroContato: contato.nroContato,
      status: 'ABERTA',
      origem: 'BALCAO',
      codFilial: contato.codFilial,
      codCliente,
      codUsuarioIntegracao: contato.codUsuario,
      // Os defaults do lote — obrigatório só o tipo; os outros dois o backend
      // resolve sozinho quando ausentes (prioridade cai no NORMAL da entidade).
      tipoRequisicaoPadrao: tipoPadraoLote,
      prioridadePadrao: prioridadePadraoLote,
      ...(obsPadraoLote.trim() ? { obsItemPadrao: obsPadraoLote.trim() } : {}),
      ...(overrides.length > 0 ? { overrides } : {}),
    };

    try {
      const resultado = await disparar({
        chaveTrava,
        executar: () => apiPost('/v1/requisicoes', payload),
        onInline: () => {
          const msg = mensagemResultado({ assincrono: false });
          limparSelecao();
          setEdicoes({});
          Toast.success(msg.texto);
          onClose();
        },
      });

      if (resultado === 'bloqueado') {
        await showAlert('Já existe uma criação de requisição em andamento para este contato.', {
          variant: 'warning',
        });
        return;
      }

      // 202: o provider avisa agora e de novo no fim. A lista não é relida aqui.
      if (resultado === 'aceito') {
        limparSelecao();
        setEdicoes({});
        onClose();
      }
    } catch (err) {
      logger.error('Erro ao criar requisição:', err);
      // A mensagem do backend passa adiante: é ela que distingue "tipoRequisicaoPadrao
      // obrigatório" de "contato sem nenhum item".
      const msg = (axios.isAxiosError(err) && err.response?.data?.error) || 'Erro ao criar a requisição. Tente novamente.';
      await showAlert(msg, { variant: 'error' });
    }
  };

  // Recarrega a lista quando um lote DESTE contato termina, se o painel ainda
  // estiver aberto. Handler em ref para a inscrição não ser refeita a cada render.
  const aoConcluirLoteRef = useRef<() => void>(() => {});
  aoConcluirLoteRef.current = () => {
    limparSelecao();
    setEdicoes({});
    reset();
  };

  useEffect(
    () => aoConcluir(chaveTrava, () => aoConcluirLoteRef.current()),
    [aoConcluir, chaveTrava],
  );

  const handleSave = async () => {
    if (modoSelecao === 'todos') {
      await dispararSelectAll();
      return;
    }

    const payload = await buildRequisicaoPayload();
    if (!payload || payload.itens.length === 0) { /* validações existentes */ return; }

    try {
      await callApi('post', '/v1/requisicoes', payload, {
        onSuccess: () => {
          Toast.success('Requisição criada com sucesso!');
          onClose();
        },
      });
    } catch (err) {
      logger.error('Erro ao criar requisição:', err);
      await showAlert('Erro ao criar a requisição. Tente novamente.', { variant: 'error' });
    }
    // Não precisa de try/finally — o hook já gerencia isSaving automaticamente
  };

  // As três colunas editáveis por item SAEM no modo 'todos' e viram campos
  // únicos no rodapé. Não é escolha estética: o conjunto resolvido inclui itens
  // que nunca foram carregados, e um campo por linha só alcançaria os visíveis —
  // o backend receberia o default do lote para o resto, e a tela estaria
  // prometendo um controle que não vale para o que ela está enviando.
  // Mesmo desenho da coluna Motivo na reprovação em lote (superfície 1).
  const colunasPorItem = modoSelecao === 'todos' ? [] : [
    {
      header: 'Tipo de Requisição',
      render: (item: ContatoItem) => (
        <select
          value={valorDe(item).tipoRequisicao}
          onChange={(e) => handleTipoRequisicaoChange(item, e.target.value)}
          className="reason-select"
        >
          {TIPOS_REQUISICAO.map((tipo: TipoRequisicao) => (
            <option key={tipo} value={tipo}>
              {tipo}
            </option>
          ))}
        </select>
      ),
    },
    {
      header: 'Prioridade',
      render: (item: ContatoItem) => (
        <select
          value={valorDe(item).prioridade}
          onChange={(e) => handlePrioridadeChange(item, e.target.value)}
          className="reason-select"
        >
          <option value="">Selecione...</option>
          {PRIORIDADES.map((prioridade: Prioridade) => (
            <option key={prioridade} value={prioridade}>
              {prioridade}
            </option>
          ))}
        </select>
      ),
    },
    {
      header: 'Observações',
      render: (item: ContatoItem) => (
        <input
          type="text"
          value={valorDe(item).observacoes}
          onChange={(e) => handleObservacoesChange(item, e.target.value)}
          maxLength={100}
          placeholder="Até 100 caracteres"
          className="observacoes-input"
        />
      ),
    },
  ];

  const columns = [
    {
      header: 'Quantidade',
      render: (item: ContatoItem) => (
        <input
          type="number"
          value={valorDe(item).quantity}
          onChange={(e) => handleQuantityChange(item, e.target.value)}
          min="0.001"
          step="0.001"
          max={item.qtdProdutoContatoSaldo}
          className="quantity-input"
        />
      ),
    },
    {
      header: 'Código',
      render: (item: ContatoItem) => item.codProduto,
    },
    {
      header: 'Cód. Categoria',
      render: (item: ContatoItem) => item.codCategoriaProduto,
    },
    {
      header: 'Nome do Produto',
      render: (item: ContatoItem) => item.nomeProduto,
    },
    {
      header: 'Categoria',
      render: (item: ContatoItem) => item.nomeCategoria,
    },
    ...colunasPorItem,
  ];

  return (
    <div className={`contact-items-modal-content ${theme}`}>
      <CloseButtonSidePanel onClick={onClose} className="close-button-absolute" disabled={isSaving} />
      <div className="title-table">
        <div>
          <div className="facelet-h3">
            <h3>
              Contato {contato.nroContato} | Gerado em {formatDate(contato.dataContato)} | Filial{' '}
              {contato.nomeFantasia || 'Erro de Parametrização'}
            </h3>
          </div>
          <div className="facelet-p">
            <p>
              Cliente: {contato.nomeCliente || 'Erro de Integração'} | Vendedor:{' '}
              {contato.nomeUsuario || 'Erro de Integração'}
            </p>
          </div>
        </div>
        <div>
          <h4>.</h4>
        </div>
      </div>
      {isTableLoading && items.length === 0 ? (
        <p>Carregando itens...</p>
      ) : (
        <>
          <ScrollableTable<ContatoItem>
            data={items}
            hasMore={hasMore}
            loadMore={loadMore}
            isLoading={isTableLoading}
            columns={columns}
            onSelectChange={handleSelectChange}
            renderAction={(item, index) => (
              <button
                className="action-button"
                ref={(el) => {
                  buttonRefs.current[index] = el;
                }}
                onClick={() => handleSaldoClick(item)}
                title="Ver Saldo por Filial"
              >
                <FontAwesomeIcon icon={faBoxesStacked} />
              </button>
            )}
            emptyMessage="Nenhum item encontrado para este contato."
            selecaoEntrePaginas={{
              estadoCabecalho,
              estaSelecionado: (it: ContatoItem) =>
                modoSelecao === 'todos'
                  ? !excludeIds.has(chaveItem(it))
                  : selectedItems.some((s) => chaveItem(s) === chaveItem(it)),
              onToggleCabecalho: handleToggleCabecalho,
              onSelecionarItem: handleSelecionarItem,
            }}
          />
          <div className="form-actions">
            {/* Escopo da seleção. Informativo — o gesto é o checkbox do cabeçalho. */}
            {estadoCabecalho !== 'vazio' && (
              <span className="contato-info-selecao">
                {textoSelecao(qtdSelecionada, totalItems)}
              </span>
            )}
            {/* Os três campos que saíram da tabela, agora únicos para o lote. */}
            {modoSelecao === 'todos' && (
              <span className="contato-campos-lote">
                <label>
                  Tipo:{' '}
                  <select
                    value={tipoPadraoLote}
                    onChange={(e) => setTipoPadraoLote(e.target.value)}
                    className="reason-select"
                    disabled={processandoLote}
                  >
                    {TIPOS_REQUISICAO.map((tipo: TipoRequisicao) => (
                      <option key={tipo} value={tipo}>{tipo}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Prioridade:{' '}
                  <select
                    value={prioridadePadraoLote}
                    onChange={(e) => setPrioridadePadraoLote(e.target.value)}
                    className="reason-select"
                    disabled={processandoLote}
                  >
                    {PRIORIDADES.map((prioridade: Prioridade) => (
                      <option key={prioridade} value={prioridade}>{prioridade}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Observações:{' '}
                  <input
                    type="text"
                    value={obsPadraoLote}
                    onChange={(e) => setObsPadraoLote(e.target.value)}
                    maxLength={100}
                    placeholder="Até 100 caracteres"
                    className="observacoes-input"
                    disabled={processandoLote}
                  />
                </label>
              </span>
            )}
            {(role === 'ROLE_MASTER' || hasPermission('requisicoes.criar')) && temSelecao && (
              <SaveButton
                isLoading={isSaving || processandoLote}
                onClick={handleSave}
                disabled={isSaving || processandoLote}
              />
            )}
          </div>
          <Modal
            isOpen={popoverOpen}
            onClose={handleCloseModal}
            theme={theme}
            zIndex={2000}
          >
            <SaldoRedeModalContent
              saldos={popoverData}
              isOpen={popoverOpen}
              onClose={handleCloseModal}
              theme={theme}
              showCancelButton={false}
            />
          </Modal>
        </>
      )}
    </div>
  );
};

export default ContactItemsSidePanelContent;
